import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  Bot,
  AlertTriangle,
  AlertOctagon,
  CheckCircle2,
  Info,
  Clock,
  Send,
} from 'lucide-react';

interface AdvisorMessage {
  id: string;
  advisor_type: 'morning_briefing' | 'anomaly' | 'recommendation' | 'weekly_summary';
  title: string;
  content: string;
  severity: 'info' | 'warning' | 'critical';
  acknowledged: boolean;
  created_at: string;
}

interface AdvisorList {
  data: AdvisorMessage[];
  total: number;
}

interface AskResponse {
  query: string;
  answer: string | null;
  data: unknown;
  source: 'ai' | 'structured';
}

const MOCK_MESSAGES: AdvisorMessage[] = [];

const SEVERITY_CONFIG: Record<string, { bg: string; text: string; icon: React.ComponentType<{ className?: string }> }> = {
  info: { bg: 'rgba(59,130,246,0.12)', text: '#3B82F6', icon: Info },
  warning: { bg: 'rgba(234,179,8,0.12)', text: '#EAB308', icon: AlertTriangle },
  critical: { bg: 'rgba(239,68,68,0.12)', text: '#EF4444', icon: AlertOctagon },
};

export default function FleetAdvisorPage() {
  const [nlQuery, setNlQuery] = useState('');
  const [askResult, setAskResult] = useState<AskResponse | null>(null);
  const [asking, setAsking] = useState(false);
  const [filterUnread, setFilterUnread] = useState(false);

  const { data: listData, isLoading, refetch } = useApi<AdvisorList>(
    `/api/fleet-advisor${filterUnread ? '?acknowledged=false' : ''}`,
  );

  const messages = listData?.data ?? MOCK_MESSAGES;
  const unread = messages.filter(m => !m.acknowledged).length;
  const critical = messages.filter(m => m.severity === 'critical').length;
  const recommendations = messages.filter(m => m.advisor_type === 'recommendation').length;
  const lastBriefing = messages.find(m => m.advisor_type === 'morning_briefing');

  async function handleAcknowledge(id: string) {
    await fetch(`/api/fleet-advisor/${id}/acknowledge`, { method: 'PUT' });
    refetch?.();
  }

  async function handleAsk() {
    if (!nlQuery.trim()) return;
    setAsking(true);
    setAskResult(null);
    try {
      const res = await fetch('/api/fleet-advisor/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: nlQuery }),
      });
      if (res.ok) {
        const result = await res.json() as AskResponse;
        setAskResult(result);
      }
    } finally {
      setAsking(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI Fleet Advisor — THEO"
        description="Morning briefings, alerts, and natural language fleet queries"
        actions={isLoading ? <Spinner size={16} /> : undefined}
      />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Bot} label="Unread Alerts" value={unread} accent="warning" />
        <StatCard icon={AlertOctagon} label="Critical Issues" value={critical} accent="destructive" />
        <StatCard icon={CheckCircle2} label="Recommendations" value={recommendations} />
        <div
          className="rounded-xl p-4 flex flex-col gap-1"
          style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(var(--glass-blur))' }}
        >
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
            <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
              Last Briefing
            </span>
          </div>
          <span className="text-sm font-medium truncate" style={{ color: 'var(--foreground)' }}>
            {lastBriefing
              ? new Date(lastBriefing.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
              : '—'}
          </span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>daily at 7 AM</span>
        </div>
      </div>

      {/* NL Query Input */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <Bot className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'var(--primary)' }} />
            <div className="flex-1 space-y-3">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>Ask THEO about your fleet</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                  Try: "vehicles with high maintenance cost", "drivers with low safety score", "overdue maintenance"
                </p>
              </div>
              <div className="flex gap-2">
                <textarea
                  value={nlQuery}
                  onChange={e => setNlQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleAsk(); } }}
                  placeholder="Ask a question about your fleet…"
                  rows={2}
                  className="flex-1 resize-none rounded-lg px-3 py-2 text-sm outline-none"
                  style={{
                    background: 'var(--input)',
                    border: '1px solid var(--glass-border)',
                    color: 'var(--foreground)',
                  }}
                />
                <button
                  onClick={() => void handleAsk()}
                  disabled={asking || !nlQuery.trim()}
                  className="px-3 py-2 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-colors"
                  style={{ background: 'var(--primary)', color: '#fff', opacity: (asking || !nlQuery.trim()) ? 0.5 : 1 }}
                >
                  {asking ? <Spinner size={14} /> : <Send className="w-4 h-4" />}
                  Ask
                </button>
              </div>

              {/* Answer */}
              {askResult && (
                <div
                  className="rounded-lg p-3 text-sm space-y-2"
                  style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)' }}
                >
                  {askResult.answer ? (
                    <p style={{ color: 'var(--foreground)' }}>{askResult.answer}</p>
                  ) : (
                    <p style={{ color: 'var(--muted-foreground)' }}>
                      Here is the data I found for: <em>{askResult.query}</em>
                    </p>
                  )}
                  {askResult.data != null && (
                    <pre
                      className="text-xs overflow-auto rounded p-2 max-h-48"
                      style={{ background: 'rgba(0,0,0,0.2)', color: 'var(--muted-foreground)' }}
                    >
                      {JSON.stringify(askResult.data, null, 2)}
                    </pre>
                  )}
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    Source: {askResult.source === 'ai' ? 'THEO AI' : 'Structured query'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Messages List */}
      <Card>
        <CardContent className="p-0">
          <div
            className="flex items-center justify-between p-4 border-b"
            style={{ borderColor: 'var(--glass-border)' }}
          >
            <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
              Advisor Messages
            </h2>
            <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--muted-foreground)' }}>
              <input
                type="checkbox"
                checked={filterUnread}
                onChange={e => setFilterUnread(e.target.checked)}
                className="rounded"
              />
              Unread only
            </label>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size={24} />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2" style={{ color: 'var(--muted-foreground)' }}>
              <Bot className="w-8 h-8 opacity-40" />
              <p className="text-sm">No messages yet — THEO will send a briefing at 7 AM daily.</p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: 'var(--glass-border)' }}>
              {messages.map(msg => {
                const sc = SEVERITY_CONFIG[msg.severity] ?? { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF', icon: Info };
                const SeverityIcon = sc.icon;
                return (
                  <div
                    key={msg.id}
                    className="p-4 flex items-start gap-4"
                    style={{ opacity: msg.acknowledged ? 0.6 : 1 }}
                  >
                    {/* Severity icon */}
                    <div
                      className="p-2 rounded-lg flex-shrink-0 mt-0.5"
                      style={{ background: sc.bg }}
                    >
                      <SeverityIcon className="w-4 h-4" style={{ color: sc.text }} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{msg.title}</p>
                          <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--muted-foreground)' }}>
                            {msg.content.split('\n')[0]}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                            {new Date(msg.created_at).toLocaleDateString()}
                          </span>
                          {!msg.acknowledged && (
                            <button
                              onClick={() => void handleAcknowledge(msg.id)}
                              className="px-2 py-0.5 rounded text-xs font-medium transition-colors"
                              style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E' }}
                            >
                              Acknowledge
                            </button>
                          )}
                          {msg.acknowledged && (
                            <span className="flex items-center gap-1 text-xs" style={{ color: '#22C55E' }}>
                              <CheckCircle2 className="w-3 h-3" />
                              Done
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
